import { isActiveGroup } from 'sonata-static-storage/lib/helpers/itemsGrouping.helpers';

import { familyAdapters } from './familyAdapters';
import { withGroups } from './groupDeletion';
import { toId } from './groupIds';

import type { GroupableItem, GroupFamily, GroupingState, GroupLike } from './grouping.types';

export interface ItemsResult<I extends GroupableItem> {
  affectedItemIds: string[];
  items: I[];
}

/** Adds the group to the given items that are not in it yet. */
export function addItems<I extends GroupableItem>(
  state: Pick<GroupingState<GroupLike, I>, 'items'>,
  family: GroupFamily,
  groupId: string,
  itemIds: string[]
): ItemsResult<I> {
  const { itemId } = familyAdapters[family];
  const targets = new Set(itemIds);
  const affectedItemIds: string[] = [];
  const items = state.items.map(item => {
    const current = (item.groups ?? []).map(toId);
    if (!targets.has(itemId(item)) || current.includes(groupId)) {
      return item;
    }
    affectedItemIds.push(itemId(item));
    return withGroups(item, [...current, groupId]);
  });
  return { affectedItemIds, items };
}

/**
 * Keeps the default group (screens only) consistent: non-deleted items without any other active group join it,
 * and the ones with another active group leave it. No-op when the family has no (active) default group.
 */
export function syncDefaultGroupMembership<I extends GroupableItem>(
  family: GroupFamily,
  groups: GroupLike[],
  items: I[]
): ItemsResult<I> {
  const adapter = familyAdapters[family];
  const defaultGroupId = adapter.defaultGroupId(groups);
  if (defaultGroupId === undefined) {
    return { affectedItemIds: [], items };
  }
  const otherActiveIds = new Set(
    groups.filter(group => isActiveGroup(group) && toId(group._id) !== defaultGroupId).map(group => toId(group._id))
  );
  const affectedItemIds: string[] = [];
  const next = items.map(item => {
    if (item.deleted) {
      return item;
    }
    const current = (item.groups ?? []).map(toId);
    const hasDefault = current.includes(defaultGroupId);
    const hasOther = current.some(id => otherActiveIds.has(id));
    if (hasDefault === !hasOther) {
      return item;
    }
    affectedItemIds.push(adapter.itemId(item));
    return withGroups(item, hasOther ? current.filter(id => id !== defaultGroupId) : [...current, defaultGroupId]);
  });
  return { affectedItemIds, items: next };
}
